import store from "../store";

import * as Utils from "./util/Utils";
import * as StorageUtils from "./util/StorageUtils";
import { StorageKey } from "./util/StorageUtils";
import * as EnvironmentUtils from "./util/EnvironmentUtils";
import * as NavigationUtils from "./util/NavigationUtils";

import * as UserSelectors from "../store/User/User.selector";
import * as GlobalActions from "../store/Global/Global.action";
import * as InsightActions from "../store/Insights/Insights.action";

export const DEMO_FLEET_ID = "__demo";
export const DEMO_FLEET_NAME = "Demo";

export interface FlagOptions<T> {
	refresh?: boolean;
	override?: () => T | undefined;
	subscribe?: (callback: () => void) => void;
	get?: (defaultValue: T | undefined) => T;
	set?: (value: T | undefined) => void;
}

export type FlagChangeListener = () => void;

export class Flag<T> {
	private readonly key: StorageKey<T>;
	readonly defaultValue: T | undefined;
	private prevValue: T | undefined;
	private readonly options: FlagOptions<T>;
	private readonly changeListeners: FlagChangeListener[] = [];

	constructor(
		id: string,
		defaultValue: T | undefined,
		options?: FlagOptions<T>,
	) {
		this.key = StorageUtils.createStorageKey(id, defaultValue);
		this.defaultValue = defaultValue;
		this.options = options || {};
		if (this.options.subscribe) {
			Utils.nextFrame(() => {
				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
				this.options.subscribe!(() => {
					if (this.prevValue !== this.get()) {
						this.triggerListeners();
					}
				});
			});
		}
	}

	toggle() {
		this.set(!this.get() as any);
	}

	get(): T | undefined {
		const { override } = this.options;
		if (override) {
			const overriden = override();
			if (overriden !== undefined) {
				return overriden;
			}
		}
		return this.getRaw();
	}

	getRaw() {
		const { get } = this.options;

		return get
			? get(this.defaultValue)
			: StorageUtils.get(this.key, this.defaultValue);
	}

	is = (): boolean => {
		return Boolean(this.get());
	};

	set(value: T | undefined) {
		const { set } = this.options;

		if (set) {
			set(value);
		} else {
			StorageUtils.set(this.key, value);
		}
		if (value !== this.prevValue) {
			this.triggerListeners();
		}
		this.prevValue = value;
	}

	reset(): void {
		const { set } = this.options;

		if (set) {
			set(undefined);
		} else {
			StorageUtils.remove(this.key);
		}
		this.triggerListeners();
		this.prevValue = undefined;
	}

	isOverridden(): boolean {
		return Boolean(
			this.options.override && this.options.override() !== undefined,
		);
	}

	addListener(listener: FlagChangeListener) {
		this.changeListeners.push(listener);
	}

	removeListener(listener: FlagChangeListener) {
		const listenerIndex = this.changeListeners.indexOf(listener);
		if (listenerIndex >= 0) {
			this.changeListeners.splice(listenerIndex, 1);
		}
	}

	private triggerListeners() {
		const { refresh } = this.options;
		this.changeListeners.forEach((listener) => listener());

		if (refresh) {
			NavigationUtils.refresh();
		}
	}
}

const ANONYMOUS_MODE_FLAG_ID = "BRIGHTMILE_ANONYMOUS_MODE";

export const FORCE_OSTRICH_MODE = new Flag(
	"BRIGHTMILE_FORCE_OSTRICH_MODE",
	undefined,
	{ refresh: true },
);
export const FRESH_VIEW = new Flag("BRIGHTMILE_FRESH_VIEW", undefined, {
	refresh: true,
});
export const SHOW_ARCHIVED_FLEETS = new Flag(
	"BRIGHTMILE_SHOW_ARCHIVED_FLEETS",
	undefined,
	{},
);
export const SHOW_ERRORS = new Flag("BRIGHTMILE_SHOW_ERRORS", undefined);
export const NETWORK_LOGGING = new Flag(
	"BRIGHTMILE_NETWORK_LOGGING",
	!EnvironmentUtils.isProductionBuild() &&
		EnvironmentUtils.isEnvironmentVariable("REACT_APP_NETWORK_LOGGER"),
);
export const REDUX_LOGGING = new Flag(
	"BRIGHTMILE_REDUX_LOGGING",
	!EnvironmentUtils.isProductionBuild() &&
		EnvironmentUtils.isEnvironmentVariable("REACT_APP_REDUX_LOGGER"),
);
export const REQUEST_CACHE = new Flag(
	"BRIGHTMILE_REQUEST_CACHE",
	EnvironmentUtils.isProductionBuild(),
	{
		override: () => (DEMO_MODE.is() ? false : undefined),
		subscribe: (callback) => {
			DEMO_MODE.addListener(callback);
		},
	},
);
export const DEBUG_FEATURES = new Flag("BRIGHTMILE_DEBUG_FEATURES", undefined, {
	refresh: true,
});
export const ENABLE_JML = new Flag("BRIGHTMILE_ENABLE_JML", undefined, {
	refresh: true,
});
export const HIDE_SUPER_ADMIN_FEATURES = new Flag(
	"BRIGHTMILE_HIDE_SUPER_ADMIN_FEATURES",
	undefined,
	{
		refresh: true,
	},
);
export const DISABLE_API_CACHE = new Flag(
	"BRIGHTMILE_DISABLE_API_CACHE",
	false,
);
export const ANONYMOUS_MODE = new Flag(ANONYMOUS_MODE_FLAG_ID, undefined, {
	override: () => DEMO_MODE.is() || undefined,
	set: (anonymousMode?: boolean) => {
		const userScope: any = UserSelectors.selectCurrentScope(store.getState());

		store.dispatch(InsightActions.getInsights({ ...userScope, anonymousMode }));
		const key = StorageUtils.createStorageKey(ANONYMOUS_MODE_FLAG_ID, false);
		StorageUtils.set(key, anonymousMode);
	},
	refresh: true,
});

export const ENABLE_MIXPANEL = new Flag(
	"BRIGHTMILE_ENABLE_MIXPANEL",
	EnvironmentUtils.getEnvironmentVariable("REACT_APP_MIXPANEL_ENABLED") ===
		"true",
);

export const HIDE_TRIP_VIEW = new Flag("BRIGHTMILE_HIDE_TRIP_VIEW", false);

// @Depreacted
// Demo mode has been removed, we should always return false
export const DEMO_MODE: Flag<boolean> = new Flag<boolean>(
	"BRIGHTMILE_DEMO_MODE",
	undefined,
	{
		get: () => false,
		set: (demoMode: boolean | undefined) => {
			const loggedInUser = UserSelectors.selectLoggedInUser(store.getState());
			store.dispatch(
				GlobalActions.setScope({
					fleetId: loggedInUser?.fleetId,
					group: loggedInUser?.group,
				}),
			);
		},
		subscribe: (callback) => {
			let prevValue: boolean | undefined = undefined;
			store.subscribe(() => {
				const current =
					UserSelectors.selectCurrentScopeFleetId(store.getState()) ===
					DEMO_FLEET_ID;
				if (prevValue !== undefined && current !== prevValue) {
					prevValue = current;
					// eslint-disable-next-line callback-return
					callback();
				}
				prevValue = current;
			});
		},
	},
);

export const isTooltipDebugMode = (): boolean => {
	return document.body.classList.contains("--tooltip-debug");
};
